// Quote form component for creating/editing quotes
import { useForm, Controller } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { quoteSchema, type QuoteInput, type Account, type Contact, type Opportunity } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { Autocomplete, type AutocompleteOption } from '../ui/Autocomplete'
import { createCrudApi, type PaginatedResponse } from '../../lib/api'
import { OwnerSelect } from './OwnerSelect'

interface Props {
  defaultValues?: Partial<QuoteInput>
  onSubmit: (data: QuoteInput) => void
  onCancel?: () => void
  loading?: boolean
  accountId?: string
  accountName?: string
  opportunityId?: string
  opportunityName?: string
}

const accountsApi = createCrudApi<Account, unknown>('accounts')
const contactsApi = createCrudApi<Contact, unknown>('contacts')
const opportunitiesApi = createCrudApi<Opportunity, unknown>('opportunities')

async function searchAccounts(search: string): Promise<AutocompleteOption[]> {
  const response: PaginatedResponse<Account> = await accountsApi.list({
    filters: { name: search },
    limit: 10,
  })
  return response.data.map((account) => ({
    id: account.id,
    label: account.name,
  }))
}

async function searchOpportunities(search: string, accountId?: string): Promise<AutocompleteOption[]> {
  const filters: Record<string, string> = { name: search }
  if (accountId) filters.accountId = accountId
  const response: PaginatedResponse<Opportunity> = await opportunitiesApi.list({
    filters,
    limit: 10,
  })
  return response.data.map((opp) => ({
    id: opp.id,
    label: opp.name,
  }))
}

// Helper to format date for datetime-local input
function formatDateForInput(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function QuoteForm({
  defaultValues,
  onSubmit,
  onCancel,
  loading,
  accountId,
  accountName,
  opportunityId,
  opportunityName,
}: Props) {
  const { t } = useTranslation()

  // Set default dates: docDate = today, validUntil = +30 days
  const today = new Date()
  const plus30days = new Date(today)
  plus30days.setDate(plus30days.getDate() + 30)

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<QuoteInput>({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      docDate: formatDateForInput(today),
      validUntil: formatDateForInput(plus30days),
      docCurrency: 'EUR',
      discountPercent: '0',
      status: 'draft',
      ...(accountId ? { accountId } : {}),
      ...(opportunityId ? { opportunityId } : {}),
      ...defaultValues,
    },
  })

  // Watch the accountId to fetch related contacts
  const selectedAccountId = watch('accountId') || accountId

  // Fetch contacts for the selected account
  const { data: contactsResponse, isLoading: contactsLoading } = useQuery({
    queryKey: ['contacts', 'byAccount', selectedAccountId],
    queryFn: () => contactsApi.list({ filters: { accountId: selectedAccountId! }, limit: 100 }),
    enabled: !!selectedAccountId,
  })

  const contacts = contactsResponse?.data || []

  // Clear contactId when account changes (except on initial load)
  useEffect(() => {
    if (selectedAccountId && selectedAccountId !== defaultValues?.accountId) {
      setValue('contactId', undefined)
    }
  }, [selectedAccountId, setValue, defaultValues?.accountId])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Account selection */}
      {!accountId && (
        <Controller
          name="accountId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              value={field.value || ''}
              onChange={(id) => field.onChange(id)}
              fetchOptions={searchAccounts}
              label={t('fields.account')}
              placeholder="Type account name..."
              required
              error={errors.accountId?.message}
              minChars={2}
              queryKey="accounts"
              initialLabel={accountName}
            />
          )}
        />
      )}

      {/* Contact selection */}
      <div>
        <label htmlFor="contactId" className="block text-sm font-medium text-gray-700">
          {t('fields.contact')}
        </label>
        <select
          {...register('contactId')}
          disabled={!selectedAccountId || contactsLoading}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
        >
          <option value="">
            {!selectedAccountId
              ? 'Select an account first...'
              : contactsLoading
              ? 'Loading contacts...'
              : contacts.length === 0
              ? 'No contacts for this account'
              : 'Select a contact...'}
          </option>
          {contacts.map((contact) => (
            <option key={contact.id} value={contact.id}>
              {[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
              {contact.title ? ` - ${contact.title}` : ''}
            </option>
          ))}
        </select>
        {errors.contactId && (
          <p className="mt-1 text-sm text-red-600">{errors.contactId.message}</p>
        )}
      </div>

      {/* Date fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="docDate" className="block text-sm font-medium text-gray-700">
            Quote Date <span className="text-red-500">*</span>
          </label>
          <input
            {...register('docDate')}
            type="datetime-local"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.docDate && (
            <p className="mt-1 text-sm text-red-600">{errors.docDate.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="validUntil" className="block text-sm font-medium text-gray-700">
            Valid Until <span className="text-red-500">*</span>
          </label>
          <input
            {...register('validUntil')}
            type="datetime-local"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.validUntil && (
            <p className="mt-1 text-sm text-red-600">{errors.validUntil.message}</p>
          )}
        </div>
      </div>

      {/* Currency and Discount */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="docCurrency" className="block text-sm font-medium text-gray-700">
            Currency
          </label>
          <select
            {...register('docCurrency')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
            <option value="CHF">CHF</option>
          </select>
          {errors.docCurrency && (
            <p className="mt-1 text-sm text-red-600">{errors.docCurrency.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="discountPercent" className="block text-sm font-medium text-gray-700">
            Document Discount %
          </label>
          <input
            {...register('discountPercent')}
            type="number"
            step="0.01"
            min="0"
            max="100"
            placeholder="0.00"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.discountPercent && (
            <p className="mt-1 text-sm text-red-600">{errors.discountPercent.message}</p>
          )}
        </div>
      </div>

      {/* Opportunity link */}
      {!opportunityId && (
        <Controller
          name="opportunityId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              value={field.value || ''}
              onChange={(id) => field.onChange(id || null)}
              fetchOptions={(search) => searchOpportunities(search, selectedAccountId)}
              label="Linked Opportunity"
              placeholder="Type opportunity name..."
              error={errors.opportunityId?.message}
              minChars={2}
              queryKey="opportunities"
              initialLabel={opportunityName}
            />
          )}
        />
      )}

      {/* Card Code/Name (for external references) */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="cardCode" className="block text-sm font-medium text-gray-700">
            Customer Code
          </label>
          <input
            {...register('cardCode')}
            type="text"
            maxLength={15}
            placeholder="External customer code"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.cardCode && (
            <p className="mt-1 text-sm text-red-600">{errors.cardCode.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="cardName" className="block text-sm font-medium text-gray-700">
            Customer Name
          </label>
          <input
            {...register('cardName')}
            type="text"
            maxLength={100}
            placeholder="External customer name"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.cardName && (
            <p className="mt-1 text-sm text-red-600">{errors.cardName.message}</p>
          )}
        </div>
      </div>

      {/* Owner assignment - only visible to admins/managers */}
      <Controller
        name="ownerId"
        control={control}
        render={({ field }) => (
          <OwnerSelect
            value={field.value}
            onChange={field.onChange}
            disabled={loading}
          />
        )}
      />

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
